import { addToolBreadcrumb } from '@tunasong/models'
import { MelodyLib, toPhraseFeedback, type CaptureDiagnosis, type VocalLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { Button, Dialog, useTunaMediaQuery, useUserConfig } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useRef, useState } from 'react'
import { useMelodyRollPlayback } from '../melody-roll/use-melody-roll-playback.js'
import { runCaptureWindow, type CapturePhase } from '../pitch-training/capture-session.js'
import { micAccessFailure, type MicAccessFailure } from '../pitch-training/mic-access.js'
import { PitchCalibrationPanel } from '../pitch-training/pitch-calibration-panel.js'
import { initialCalibration, type CalibrationState } from '../pitch-training/pitch-calibration.js'
import { PitchFeedbackPanel } from '../pitch-training/pitch-feedback-panel.js'
import { PitchMicBlockedPanel } from '../pitch-training/pitch-mic-blocked-panel.js'
import { leadInMs, usePitchAudition } from '../pitch-training/pitch-training-playback.js'
import { runCalibrationTake } from '../pitch-training/run-calibration-take.js'
import { useCaptureMonitor } from '../pitch-training/use-capture-monitor.js'
import { usePitchCapture } from '../pitch-training/use-pitch-capture.js'
import { Audition } from './audition.js'
import { feedbackView } from './feedback-view.js'
import { PhraseFeedback } from './phrase-feedback.js'
import { PhrasePicker } from './phrase-picker.js'
import { PhraseQuestion } from './phrase-question.js'
import { PhraseRefusal } from './phrase-refusal.js'
import { buildPickerItems, type PickerItem, type PracticeSource } from './picker-items.js'
import { prepareTarget, type PracticeTarget, type PreparedTarget, type Singable } from './practice-target.js'
import { scoreCapture } from './score-capture.js'
import styles from './phrase-practice-dialog.module.css'

/**
 * "Sing a phrase" over the song (Pitch Trainer Phase 2, plans M5 and M6a): the picker, then the phrase's
 * question, take, feedback, refusal and split, in a dialog like Melody Trainer, so the singer never leaves
 * the song. Escape returns to it.
 *
 * Every take is started by a click, never by an effect, as in Phase 1: a browser resumes audio only on a
 * gesture, and an effect that re-fired would reopen the microphone mid-take.
 *
 * It works from a snapshot of the sources taken when it opened (plan 5.3). An edit to the song while it is
 * open does not move the list under the singer; it says `This section changed.` and offers a reload.
 */

export interface PhrasePracticeDialogProps {
  open: boolean
  onClose: () => void
  /** The whole song's sources, in song order */
  sources: PracticeSource[]
  /** Opened from a section: start on it, with the scope control. Absent from a melody block */
  scope?: { sectionName: string; initial: 'section' | 'song' }
  /** Pitfall 6: close and put the cursor on the source */
  onEdit: (item: PickerItem) => void
}

type Refused = Extract<PreparedTarget, { kind: 'refused' }>

type Stage =
  | { kind: 'pick' }
  | { kind: 'question'; target: PracticeTarget; phase: CapturePhase | null; queue: Singable[] }
  | {
      kind: 'feedback'
      target: PracticeTarget
      score: VocalLib.PhraseScore | null
      diagnosis: CaptureDiagnosis | null
      queue: Singable[]
    }
  | { kind: 'refused'; refused: Refused }
  | {
      kind: 'calibrating'
      calibration: CalibrationState
      listening: boolean
      diagnosis: CaptureDiagnosis | null
      /** Joni §4: calibration from a refusal goes straight back to that phrase */
      returnTo: Singable | null
    }
  | { kind: 'mic-blocked'; failure: MicAccessFailure; repeated: boolean; retry: MicRetry }

type MicRetry =
  | { from: 'take'; target: PracticeTarget; queue: Singable[] }
  | { from: 'calibration'; calibration: CalibrationState; returnTo: Singable | null }

/** Joni §5: twice the reference plus a second, at most 20 s */
const captureCapMs = (audition: Melody) => Math.min(2 * MelodyLib.melodyDurationMs(audition) + 1000, 20_000)

const silenceEndsTakeMs = 1000

const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms))

const emptyMelody: Melody = {
  type: 'melody',
  tempo: { bpm: 100, meter: { top: 4, bottom: 4 } },
  grid: '16',
  notes: [],
}

/** What the song held, for comparing a snapshot with the live sources */
const fingerprint = (sources: PracticeSource[]) =>
  JSON.stringify(
    sources.map(source => (source.kind === 'melody' ? [source.id, source.melody.notes] : [source.id, source.lines]))
  )

const PracticeBody: FC<Omit<PhrasePracticeDialogProps, 'open' | 'onClose'>> = props => {
  const { sources, scope, onEdit } = props
  const [userConfig, updateConfig] = useUserConfig()
  const range = userConfig?.musicPreferences?.vocalRange ?? null
  const [snapshot, setSnapshot] = useState(sources)
  const [scopeValue, setScopeValue] = useState<'section' | 'song'>(scope?.initial ?? 'song')
  const [stage, setStage] = useState<Stage>({ kind: 'pick' })
  /** Each Hear it is a new take, so pressing it again replays */
  const [hearing, setHearing] = useState<{ melody: Melody; take: number } | null>(null)
  const done = useRef<AbortController | null>(null)

  const { setMuted, startFrames, deviceLabel, sampleRate } = usePitchCapture()
  const monitor = useCaptureMonitor()
  const { playCountIn } = usePitchAudition()
  const audition = stage.kind === 'question' || stage.kind === 'feedback' ? stage.target.audition : emptyMelody
  const playback = useMelodyRollPlayback({ melody: audition })
  const meter = { frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel }

  const isChanged = fingerprint(snapshot) !== fingerprint(sources)
  const inScope =
    scope && scopeValue === 'section' ? snapshot.filter(source => source.section === scope.sectionName) : snapshot
  const items = buildPickerItems(inScope, range)

  const hear = (melody: Melody) => setHearing(previous => ({ melody, take: (previous?.take ?? 0) + 1 }))

  /** A chosen row or half: its question, or its refusal */
  const choose = (singable: PickerItem | Singable, queue: Singable[] = []) => {
    const prepared = prepareTarget(singable, range)
    setStage(
      prepared.kind === 'ready'
        ? { kind: 'question', target: prepared.target, phase: null, queue }
        : { kind: 'refused', refused: prepared }
    )
  }

  /**
   * A microphone that would not open puts the singer on the blocked screen with a way back (Phase 1, GH
   * #2327). Anything that is not about microphone access is rethrown.
   */
  const orMicFailure =
    (retry: MicRetry, previous?: MicAccessFailure) =>
    (error: unknown): null => {
      const failure = micAccessFailure(error)
      if (!failure) {
        throw error
      }
      addToolBreadcrumb('mic-unavailable', { tool: 'pitch-trainer-phrases', failure })
      setStage({ kind: 'mic-blocked', failure, repeated: failure === previous, retry })
      return null
    }

  const runTake = async (target: PracticeTarget, queue: Singable[], previous?: MicAccessFailure) => {
    monitor.reset()
    const controller = new AbortController()
    done.current = controller
    setStage({ kind: 'question', target, phase: null, queue })
    const frames = await runCaptureWindow(
      {
        setMuted,
        startFrames,
        playReference: playback.playToEnd,
        playCountIn,
        wait,
        onPhase: phase =>
          setStage(current =>
            current.kind === 'question' && current.target === target ? { ...current, phase } : current
          ),
        onFrame: monitor.onFrame,
      },
      {
        leadInMs,
        captureMs: captureCapMs(target.audition),
        earlyStop: { silenceMs: silenceEndsTakeMs, done: controller.signal },
      }
    ).catch(orMicFailure({ from: 'take', target, queue }, previous))
    done.current = null
    if (!frames) {
      return
    }
    const { score, diagnosis } = scoreCapture(frames, target, sampleRate)
    addToolBreadcrumb('sang', { tool: 'pitch-trainer-phrases', heard: score !== null, kind: target.singable.kind })
    setStage({ kind: 'feedback', target, score, diagnosis, queue })
  }

  const runRangeTake = async (
    calibration: CalibrationState,
    returnTo: Singable | null,
    previous?: MicAccessFailure
  ) => {
    monitor.reset()
    setStage({ kind: 'calibrating', calibration, listening: true, diagnosis: null, returnTo })
    const outcome = await runCalibrationTake(
      { setMuted, startFrames, wait, onFrame: monitor.onFrame },
      calibration
    ).catch(orMicFailure({ from: 'calibration', calibration, returnTo }, previous))
    if (!outcome) {
      return
    }
    setStage({
      kind: 'calibrating',
      calibration: outcome.next,
      listening: false,
      diagnosis: outcome.diagnosis,
      returnTo,
    })
    if (outcome.range) {
      updateConfig('musicPreferences', { ...userConfig?.musicPreferences, vocalRange: outcome.range })
    }
  }

  const measureRange = (returnTo: Singable | null) =>
    setStage({ kind: 'calibrating', calibration: initialCalibration, listening: false, diagnosis: null, returnTo })

  /** After a take: the next half if one is queued (Joni §8), else back to the picker */
  const next = (queue: Singable[]) => {
    const [following, ...rest] = queue
    if (following) {
      choose(following, rest)
      return
    }
    setStage({ kind: 'pick' })
  }

  const hearingAudition = hearing ? <Audition key={hearing.take} melody={hearing.melody} /> : null

  if (stage.kind === 'question') {
    return (
      <div className={styles.body}>
        <PhraseQuestion
          target={stage.target}
          phase={stage.phase}
          meter={meter}
          captureMs={captureCapMs(stage.target.audition)}
          onStart={() => void runTake(stage.target, stage.queue)}
          onDone={() => done.current?.abort()}
          onHearIt={playback.play}
        />
      </div>
    )
  }

  if (stage.kind === 'feedback') {
    const { target, score, diagnosis, queue } = stage
    const view = feedbackView(score, target, range)
    return (
      <div className={styles.body}>
        {view ? (
          <PhraseFeedback
            view={view}
            audition={target.audition}
            onAgain={() => setStage({ kind: 'question', target, phase: null, queue })}
            onNext={() => next(queue)}
            onHear={hear}
            onSingHalf={([first, ...rest]) => {
              if (first) {
                choose(first, rest)
              }
            }}
          />
        ) : (
          /** Nothing heard: Phase 1's diagnosed no-voice state, never a verdict (pitfall 9) */
          <PitchFeedbackPanel
            feedback={toPhraseFeedback(null)}
            diagnosis={diagnosis}
            onRetry={() => setStage({ kind: 'question', target, phase: null, queue })}
            onNext={() => next(queue)}
          />
        )}
        {hearingAudition}
      </div>
    )
  }

  if (stage.kind === 'refused' && range) {
    const { refused } = stage
    return (
      <div className={styles.body}>
        <PhraseRefusal
          refused={refused}
          range={range}
          onSing={half => choose(half)}
          onPickAnother={() => setStage({ kind: 'pick' })}
          onMeasureRange={() => measureRange(refused.singable)}
          onCancel={() => setStage({ kind: 'pick' })}
        />
      </div>
    )
  }

  if (stage.kind === 'calibrating') {
    const { calibration, returnTo } = stage
    const back = () => (returnTo ? choose(returnTo) : setStage({ kind: 'pick' }))
    return (
      <div className={styles.body}>
        <PitchCalibrationPanel
          state={calibration}
          listening={stage.listening}
          diagnosis={stage.diagnosis}
          meter={meter}
          onSing={() => void runRangeTake(calibration, returnTo)}
          onCancel={back}
          onDone={back}
        />
      </div>
    )
  }

  if (stage.kind === 'mic-blocked') {
    const { failure, retry } = stage
    return (
      <div className={styles.body}>
        <PitchMicBlockedPanel
          failure={failure}
          repeated={stage.repeated}
          cancelLabel="Cancel"
          onRetry={() =>
            retry.from === 'take'
              ? void runTake(retry.target, retry.queue, failure)
              : void runRangeTake(retry.calibration, retry.returnTo, failure)
          }
          onCancel={() => setStage({ kind: 'pick' })}
        />
      </div>
    )
  }

  return (
    <div className={styles.body}>
      {isChanged ? (
        <div role="status" className={styles.changed}>
          <span>This section changed.</span>
          <Button size="small" variant="outlined" onClick={() => setSnapshot(sources)}>
            Reload phrases
          </Button>
        </div>
      ) : null}
      <PhrasePicker
        /* A new list (scope or reload) starts again on its own Suggested row */
        key={`${scopeValue}:${fingerprint(snapshot)}`}
        items={items}
        isCalibrated={range !== null}
        scope={scope ? { sectionName: scope.sectionName, value: scopeValue, onChange: setScopeValue } : undefined}
        onHear={item => hear(item.thumbnail)}
        onSing={item => choose(item)}
        onEdit={onEdit}
        onMeasureRange={() => measureRange(null)}
      />
      {hearingAudition}
    </div>
  )
}

export const PhrasePracticeDialog: FC<PhrasePracticeDialogProps> = props => {
  const { open, onClose, ...body } = props
  /** Joni §2: full screen on a phone */
  const isPhone = useTunaMediaQuery('(max-width:599.95px)')
  return (
    <Dialog open={open} onClose={onClose} title="Sing a phrase" fullWidth={true} maxWidth="md" fullScreen={isPhone}>
      {/* Keyed on `open`, so each visit takes a fresh snapshot */}
      {open ? <PracticeBody key={String(open)} {...body} /> : null}
    </Dialog>
  )
}
